import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { LargeTitleHeader } from '@/components/common';
import apiClient from '@/lib/api/client';
import { getForecast, weatherIcon, type DayForecast } from '@/lib/api/weather';

// ── Task planner — a Trello-style board over the schedule ───────────────────
// Columns are days (plus an Unscheduled lane); cards are schedule_subtasks.
// Drag a card between days (desktop) or tap it and pick a date (everywhere).
// Every card belongs to a job, so it shows up automatically in the portals of
// workers attributed to that job — and they check cards off when logging hours.

interface Card {
  id: number;
  schedule_id: number;
  title: string;
  description?: string | null;
  status: 'pending' | 'in_progress' | 'completed' | 'blocked';
  assigned_to?: number | null;
  assignee_name?: string | null;
  display_order: number;
  start_date: string | null;
  job_id: number;
  job_title: string;
  approval_status?: 'approved' | 'pending' | 'rejected';
  suggested_by_name?: string | null;
  approval_note?: string | null;
  checklist_total?: number;
  checklist_done?: number;
}
interface ChecklistItem { id: number; text: string; checked: number }
interface JobOpt { id: number; title: string }
interface TeamOpt { user_id: number; name: string }

const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const day = (s?: string | null) => (s || '').slice(0, 10);

// Stable job color from id — identity, not status
const JOB_HUES = ['#C77D3F', '#5C7F4F', '#4A6FA5', '#8B5A2B', '#7B5EA7', '#B8462E', '#2A7F7F', '#A56A4A'];
const jobColor = (id: number) => JOB_HUES[id % JOB_HUES.length];

const STATUS_META: Record<Card['status'], { label: string; cls: string }> = {
  pending:     { label: 'To do',       cls: 'bg-gray-100 text-gray-600' },
  in_progress: { label: 'In progress', cls: 'bg-blue-100 text-blue-700' },
  completed:   { label: 'Done',        cls: 'bg-green-100 text-green-700' },
  blocked:     { label: 'Blocked',     cls: 'bg-red-100 text-red-700' },
};

export const PlannerPage = () => {
  const [weekStart, setWeekStart] = useState(() => {
    const n = new Date();
    const mon = new Date(n);
    mon.setDate(n.getDate() - ((n.getDay() + 6) % 7)); // Monday of this week
    return mon;
  });
  const [cards, setCards] = useState<Card[]>([]);
  const [jobs, setJobs] = useState<JobOpt[]>([]);
  const [loading, setLoading] = useState(true);
  const [editCard, setEditCard] = useState<Card | null>(null);
  const [addingCol, setAddingCol] = useState<string | null>(null); // date key or 'unscheduled'
  const [newTitle, setNewTitle] = useState('');
  const [newJobId, setNewJobId] = useState<number | null>(null);
  const dragId = useRef<number | null>(null);
  const [dragOverCol, setDragOverCol] = useState<string | null>(null);
  const [forecast, setForecast] = useState<Map<string, DayForecast>>(new Map());
  useEffect(() => {
    getForecast().then(days => setForecast(new Map(days.map(d => [d.date, d])))).catch(() => {});
  }, []);

  const today = ymd(new Date());

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    return d;
  }), [weekStart]);
  const from = ymd(days[0]), to = ymd(days[6]);
  // Ordered POs landing in the visible week
  interface Delivery { id: number; po_number: string; expected_date: string; job_title?: string; vendor_display?: string; vendor_name?: string }
  const [deliveries, setDeliveries] = useState<Map<string, Delivery[]>>(new Map());
  useEffect(() => {
    apiClient.get(`/purchase-orders?from=${from}&to=${to}`).then(r => {
      const m = new Map<string, Delivery[]>();
      for (const po of (r.data.data ?? []) as (Delivery & { status: string })[]) {
        if (po.status !== 'ordered' || !po.expected_date) continue;
        const key = String(po.expected_date).slice(0, 10);
        (m.get(key) || m.set(key, []).get(key)!).push(po);
      }
      setDeliveries(m);
    }).catch(() => {});
  }, [from, to]);

  const load = useCallback(async () => {
    try {
      const r = await apiClient.get(`/planner/board?from=${from}&to=${to}`);
      setCards(r.data.data ?? []);
    } catch { /* keep last board */ }
    finally { setLoading(false); }
  }, [from, to]);
  useEffect(() => { setLoading(true); load(); }, [load]);

  useEffect(() => {
    apiClient.get('/jobs?limit=200').then(r => {
      const d = r.data?.data ?? r.data;
      const list = (Array.isArray(d) ? d : (Array.isArray(d?.jobs) ? d.jobs : [])) as (JobOpt & { status?: string })[];
      const active = list.filter(j => !['completed', 'cancelled'].includes((j.status || '').toLowerCase()));
      setJobs(active.map(j => ({ id: j.id, title: j.title })));
      setNewJobId(prev => prev ?? active[0]?.id ?? null);
    }).catch(() => {});
  }, []);

  const byCol = useMemo(() => {
    const m = new Map<string, Card[]>();
    for (const c of cards) {
      const key = c.start_date ? day(c.start_date) : 'unscheduled';
      const arr = m.get(key) || [];
      arr.push(c);
      m.set(key, arr);
    }
    for (const arr of m.values()) arr.sort((a, b) => a.display_order - b.display_order || a.id - b.id);
    return m;
  }, [cards]);

  const moveCard = async (id: number, date: string | null) => {
    const prev = cards;
    setCards(cs => cs.map(c => c.id === id ? { ...c, start_date: date } : c));
    try {
      const r = await apiClient.patch(`/planner/tasks/${id}`, { start_date: date });
      if (r.data.data) setCards(cs => cs.map(c => c.id === id ? r.data.data : c));
    } catch { setCards(prev); }
  };

  const setStatus = async (card: Card, status: Card['status']) => {
    const prev = cards;
    setCards(cs => cs.map(c => c.id === card.id ? { ...c, status } : c));
    try { await apiClient.patch(`/planner/tasks/${card.id}`, { status }); }
    catch { setCards(prev); }
  };

  const quickAdd = async (colKey: string) => {
    const title = newTitle.trim();
    if (!title || !newJobId) return;
    setNewTitle('');
    try {
      const r = await apiClient.post('/planner/tasks', {
        job_id: newJobId,
        date: colKey === 'unscheduled' ? today : colKey,
        title,
      });
      let card: Card = r.data.data;
      if (colKey === 'unscheduled') {
        const moved = await apiClient.patch(`/planner/tasks/${card.id}`, { start_date: null });
        card = moved.data.data ?? { ...card, start_date: null };
      }
      setCards(cs => [...cs, card]);
    } catch { /* leave input cleared; board reload will reconcile */ }
  };

  const removeCard = async (id: number) => {
    if (!window.confirm('Delete this task?')) return;
    setCards(cs => cs.filter(c => c.id !== id));
    setEditCard(null);
    try { await apiClient.delete(`/planner/tasks/${id}`); } catch { load(); }
  };

  const shiftCard = (card: Card, deltaDays: number) => {
    const base = card.start_date ? new Date(day(card.start_date) + 'T12:00:00') : new Date();
    base.setDate(base.getDate() + deltaDays);
    moveCard(card.id, ymd(base));
  };

  const columns: { key: string; label: string; sub?: string; isToday?: boolean }[] = [
    { key: 'unscheduled', label: 'Unscheduled', sub: 'no day yet' },
    ...days.map(d => ({
      key: ymd(d),
      label: d.toLocaleDateString('en-CA', { weekday: 'short' }),
      sub: d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' }),
      isToday: ymd(d) === today,
    })),
  ];

  return (
    <MainLayout noPadding>
      <div className="h-full flex flex-col">
        <div className="px-2 md:px-6 pt-3">
          <LargeTitleHeader
            title="Task Planner"
            subtitle="Drag cards between days — your crew sees them in their portal and checks them off with their hours."
            actions={
              <div className="flex items-center gap-1.5">
                <button onClick={() => setWeekStart(w => { const d = new Date(w); d.setDate(d.getDate() - 7); return d; })}
                  className="h-9 w-9 rounded-lg border border-gray-200 bg-white text-gray-600 flex items-center justify-center active:bg-gray-100">
                  <span className="material-symbols-outlined">chevron_left</span>
                </button>
                <button onClick={() => { const n = new Date(); const mon = new Date(n); mon.setDate(n.getDate() - ((n.getDay() + 6) % 7)); setWeekStart(mon); }}
                  className="h-9 px-3 rounded-lg border border-gray-200 bg-white text-sm font-semibold text-gray-700 active:bg-gray-100">
                  Today
                </button>
                <button onClick={() => setWeekStart(w => { const d = new Date(w); d.setDate(d.getDate() + 7); return d; })}
                  className="h-9 w-9 rounded-lg border border-gray-200 bg-white text-gray-600 flex items-center justify-center active:bg-gray-100">
                  <span className="material-symbols-outlined">chevron_right</span>
                </button>
              </div>
            }
          />
          <p className="text-xs text-gray-400 -mt-2 mb-2 flex items-center gap-2">
            <span>{days[0].toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })} – {days[6].toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
            {cards.some(c => c.approval_status === 'pending') && (
              <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                {cards.filter(c => c.approval_status === 'pending').length} suggestion{cards.filter(c => c.approval_status === 'pending').length !== 1 ? 's' : ''} to review
              </span>
            )}
          </p>
        </div>

        {/* Board */}
        <div className="flex-1 min-h-0 overflow-x-auto overflow-y-hidden px-2 md:px-6 pb-3">
          <div className="flex gap-2.5 h-full min-w-max">
            {columns.map(col => {
              const colCards = byCol.get(col.key) || [];
              const isOver = dragOverCol === col.key;
              return (
                <div
                  key={col.key}
                  onDragOver={e => { e.preventDefault(); setDragOverCol(col.key); }}
                  onDragLeave={() => setDragOverCol(c => (c === col.key ? null : c))}
                  onDrop={e => {
                    e.preventDefault();
                    setDragOverCol(null);
                    if (dragId.current != null) moveCard(dragId.current, col.key === 'unscheduled' ? null : col.key);
                    dragId.current = null;
                  }}
                  className={`w-[16.5rem] flex-shrink-0 flex flex-col rounded-xl border transition-colors ${
                    isOver ? 'border-[#C77D3F] bg-[#C77D3F]/5' : col.isToday ? 'border-[#C77D3F]/40 bg-[#FAF6F0]' : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="px-3 py-2 flex items-center gap-2 flex-shrink-0">
                    <span className={`text-sm font-bold ${col.isToday ? 'text-[#C77D3F]' : 'text-gray-800'}`}>{col.label}</span>
                    {col.sub && <span className="text-[11px] text-gray-400">{col.sub}</span>}
                    {col.isToday && <span className="text-[9px] font-bold bg-[#C77D3F] text-white rounded-full px-1.5 py-0.5">TODAY</span>}
                    <span className="ml-auto flex items-center gap-1">
                      {(() => {
                        const w = col.key !== 'unscheduled' ? forecast.get(col.key) : undefined;
                        if (!w) return null;
                        return (
                          <span
                            title={`${w.t_min}–${w.t_max}°C · ${w.precip_prob}% precip (${w.precip_mm}mm)${w.snow_cm ? ` · ${w.snow_cm}cm snow` : ''}${w.wind_kmh >= 40 ? ` · wind ${Math.round(w.wind_kmh)}km/h` : ''}${w.risk !== 'none' ? ' — watch concrete pours & roofing' : ''}`}
                            className={`flex items-center gap-0.5 text-[9px] font-bold rounded-full px-1.5 py-0.5 ${
                              w.risk === 'high' ? 'bg-red-100 text-red-700' : w.risk === 'caution' ? 'bg-amber-100 text-amber-800' : 'text-gray-400'
                            }`}
                          >
                            <span className="material-symbols-outlined text-[11px] leading-none">{weatherIcon(w.code)}</span>
                            {w.t_max}°{w.precip_prob >= 30 ? ` ${w.precip_prob}%` : ''}
                          </span>
                        );
                      })()}
                      <span className="text-[11px] font-semibold text-gray-400">{colCards.length}</span>
                    </span>
                  </div>

                  <div className="flex-1 min-h-0 overflow-y-auto px-2 pb-2 space-y-1.5">
                    {(deliveries.get(col.key) || []).map(d => (
                      <div key={`po-${d.id}`}
                        title={`${d.po_number} — ${d.vendor_display || d.vendor_name || 'vendor'}${d.job_title ? ` → ${d.job_title}` : ''}`}
                        className="bg-blue-50 border border-blue-200 rounded-lg px-2 py-1 flex items-center gap-1">
                        <span className="material-symbols-outlined text-sm text-blue-500">local_shipping</span>
                        <span className="text-[10px] font-bold text-blue-800 truncate">
                          {d.po_number} · {d.vendor_display || d.vendor_name || 'delivery'}{d.job_title ? ` → ${d.job_title}` : ''}
                        </span>
                      </div>
                    ))}
                    {colCards.map(card => (
                      <div
                        key={card.id}
                        draggable
                        onDragStart={() => { dragId.current = card.id; }}
                        onDragEnd={() => { dragId.current = null; setDragOverCol(null); }}
                        onClick={() => setEditCard(card)}
                        className={`bg-white rounded-lg border shadow-sm p-2.5 cursor-pointer active:scale-[0.99] select-none ${
                          card.approval_status === 'pending' ? 'border-amber-400 ring-1 ring-amber-200' : 'border-gray-200'
                        } ${card.status === 'completed' ? 'opacity-60' : ''}`}
                      >
                        {card.approval_status === 'pending' && (
                          <p className="text-[9px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 mb-1 inline-flex items-center gap-1">
                            <span className="material-symbols-outlined text-[10px] leading-none">pending_actions</span>
                            SUGGESTED{card.suggested_by_name ? ` · ${card.suggested_by_name}` : ''} — tap to review
                          </p>
                        )}
                        <div className="flex items-start gap-1.5">
                          <button
                            onClick={e => { e.stopPropagation(); setStatus(card, card.status === 'completed' ? 'pending' : 'completed'); }}
                            title={card.status === 'completed' ? 'Mark not done' : 'Mark done'}
                            className="mt-0.5 flex-shrink-0"
                          >
                            <span className={`material-symbols-outlined text-lg ${card.status === 'completed' ? 'text-green-600' : 'text-gray-300 hover:text-green-500'}`}>
                              {card.status === 'completed' ? 'check_circle' : 'radio_button_unchecked'}
                            </span>
                          </button>
                          <p className={`text-[13px] font-medium leading-snug flex-1 ${card.status === 'completed' ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                            {card.title}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                          <span className="text-[10px] font-bold text-white rounded px-1.5 py-0.5 truncate max-w-[9rem]"
                            style={{ backgroundColor: jobColor(card.job_id) }}>
                            {card.job_title}
                          </span>
                          {card.status !== 'pending' && card.status !== 'completed' && (
                            <span className={`text-[10px] font-semibold rounded px-1.5 py-0.5 ${STATUS_META[card.status].cls}`}>{STATUS_META[card.status].label}</span>
                          )}
                          {card.assignee_name && (
                            <span className="text-[10px] text-gray-500 flex items-center gap-0.5">
                              <span className="material-symbols-outlined text-[11px]">person</span>{card.assignee_name}
                            </span>
                          )}
                          {(card.checklist_total || 0) > 0 && (
                            <span className={`text-[10px] font-semibold flex items-center gap-0.5 ${card.checklist_done === card.checklist_total ? 'text-green-700' : 'text-gray-500'}`}>
                              <span className="material-symbols-outlined text-[11px]">checklist</span>
                              {card.checklist_done || 0}/{card.checklist_total}
                            </span>
                          )}
                          {/* Touch-friendly day shift */}
                          <span className="ml-auto flex items-center">
                            <button onClick={e => { e.stopPropagation(); shiftCard(card, -1); }} title="Move a day earlier"
                              className="h-6 w-6 text-gray-300 hover:text-gray-600 flex items-center justify-center">
                              <span className="material-symbols-outlined text-base">chevron_left</span>
                            </button>
                            <button onClick={e => { e.stopPropagation(); shiftCard(card, 1); }} title="Move a day later"
                              className="h-6 w-6 text-gray-300 hover:text-gray-600 flex items-center justify-center">
                              <span className="material-symbols-outlined text-base">chevron_right</span>
                            </button>
                          </span>
                        </div>
                      </div>
                    ))}
                    {loading && colCards.length === 0 && <div className="h-14 rounded-lg bg-gray-100 animate-pulse" />}
                  </div>

                  {/* Quick add */}
                  <div className="p-2 pt-0 flex-shrink-0">
                    {addingCol === col.key ? (
                      <div className="bg-white rounded-lg border border-[#C77D3F]/40 p-2 space-y-1.5">
                        <input
                          autoFocus
                          value={newTitle}
                          onChange={e => setNewTitle(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') quickAdd(col.key); if (e.key === 'Escape') setAddingCol(null); }}
                          placeholder="Task for the crew…"
                          className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-sm focus:outline-none"
                        />
                        <select
                          value={newJobId ?? ''}
                          onChange={e => setNewJobId(e.target.value ? Number(e.target.value) : null)}
                          className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-xs bg-white focus:outline-none"
                        >
                          {jobs.map(j => <option key={j.id} value={j.id}>{j.title}</option>)}
                        </select>
                        <div className="flex gap-1.5">
                          <button onClick={() => quickAdd(col.key)} disabled={!newTitle.trim() || !newJobId}
                            className="flex-1 h-8 rounded-md bg-[#C77D3F] text-white text-xs font-bold disabled:opacity-40">Add</button>
                          <button onClick={() => setAddingCol(null)}
                            className="h-8 px-2.5 rounded-md border border-gray-200 text-xs text-gray-500">✕</button>
                        </div>
                      </div>
                    ) : (
                      <button onClick={() => { setAddingCol(col.key); setNewTitle(''); }}
                        className="w-full h-8 rounded-lg border border-dashed border-gray-300 text-xs font-semibold text-gray-400 hover:text-[#C77D3F] hover:border-[#C77D3F]/50 flex items-center justify-center gap-1">
                        <span className="material-symbols-outlined text-sm">add</span> Add task
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {editCard && (
        <CardModal
          card={editCard}
          onClose={() => setEditCard(null)}
          onSaved={c => {
            setCards(cs => c.approval_status === 'rejected' ? cs.filter(x => x.id !== c.id) : cs.map(x => x.id === c.id ? c : x));
            setEditCard(null);
          }}
          onDelete={() => removeCard(editCard.id)}
        />
      )}
    </MainLayout>
  );
};

// ── Card edit modal (also the mobile way to move a card to any date) ──
function CardModal({ card, onClose, onSaved, onDelete }: {
  card: Card;
  onClose: () => void;
  onSaved: (c: Card) => void;
  onDelete: () => void;
}) {
  const [title, setTitle] = useState(card.title);
  const [desc, setDesc] = useState(card.description || '');
  const [date, setDate] = useState(day(card.start_date) || '');
  const [status, setStatusV] = useState<Card['status']>(card.status);
  const [assignee, setAssignee] = useState<number | ''>(card.assigned_to || '');
  const [team, setTeam] = useState<TeamOpt[]>([]);
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState<ChecklistItem[]>([]);
  const [newItem, setNewItem] = useState('');
  // CR #154 — optional reply sent back to whoever suggested the card
  const [replyNote, setReplyNote] = useState(card.approval_note || '');

  const loadItems = () =>
    apiClient.get(`/planner/tasks/${card.id}/checklist`).then(r => setItems(r.data.data ?? [])).catch(() => {});
  useEffect(() => { loadItems(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card.id]);

  const addItem = async () => {
    const text = newItem.trim();
    if (!text) return;
    setNewItem('');
    try { await apiClient.post(`/planner/tasks/${card.id}/checklist`, { text }); loadItems(); } catch { /* ignore */ }
  };
  const toggleItem = async (it: ChecklistItem) => {
    setItems(is => is.map(x => x.id === it.id ? { ...x, checked: it.checked ? 0 : 1 } : x));
    try { await apiClient.patch(`/planner/checklist/${it.id}`, { checked: !it.checked }); } catch { loadItems(); }
  };

  useEffect(() => {
    apiClient.get(`/jobs/${card.job_id}/team`)
      .then(r => {
        const t = (r.data.data ?? []) as { user_id: number | null; employee_name?: string; username?: string }[];
        setTeam(t.filter(m => m.user_id).map(m => ({ user_id: m.user_id!, name: m.employee_name || m.username || `User ${m.user_id}` })));
      })
      .catch(() => {});
  }, [card.job_id]);

  const save = async (approval?: 'approved' | 'rejected') => {
    setBusy(true);
    try {
      const r = await apiClient.patch(`/planner/tasks/${card.id}`, {
        title: title.trim() || card.title,
        description: desc.trim() || null,
        start_date: date || null,
        status,
        assigned_to: assignee === '' ? null : assignee,
        ...(approval ? { approval_status: approval, approval_note: replyNote.trim() || null } : {}),
      });
      onSaved(r.data.data ?? { ...card, title, description: desc, start_date: date || null, status, approval_status: approval ?? card.approval_status });
    } catch { setBusy(false); }
  };

  const isSuggestion = card.approval_status === 'pending';

  const field = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C77D3F]/40';

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-5 space-y-3" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold text-white rounded px-1.5 py-0.5" style={{ backgroundColor: jobColor(card.job_id) }}>
            {card.job_title}
          </span>
          <button onClick={onDelete} title="Delete task"
            className="ml-auto h-8 w-8 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 flex items-center justify-center">
            <span className="material-symbols-outlined text-lg">delete</span>
          </button>
        </div>
        <input value={title} onChange={e => setTitle(e.target.value)} className={`${field} font-semibold`} placeholder="Task title" />
        <textarea value={desc} onChange={e => setDesc(e.target.value)} rows={2} className={`${field} resize-none`}
          placeholder="Details for the crew (optional)" />
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="text-xs font-medium text-gray-500">Day</span>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} className={`${field} mt-1`} />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-gray-500">Status</span>
            <select value={status} onChange={e => setStatusV(e.target.value as Card['status'])} className={`${field} mt-1 bg-white`}>
              <option value="pending">To do</option>
              <option value="in_progress">In progress</option>
              <option value="completed">Done</option>
              <option value="blocked">Blocked</option>
            </select>
          </label>
        </div>
        <label className="block">
          <span className="text-xs font-medium text-gray-500">Assign to (crew on this job)</span>
          <select value={assignee} onChange={e => setAssignee(e.target.value ? Number(e.target.value) : '')} className={`${field} mt-1 bg-white`}>
            <option value="">Whole crew</option>
            {team.map(t => <option key={t.user_id} value={t.user_id}>{t.name}</option>)}
          </select>
        </label>
        {/* Sub-tasks (checklist) */}
        <div className="border border-gray-100 rounded-xl p-2.5 space-y-1">
          <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400 flex items-center gap-1">
            <span className="material-symbols-outlined text-sm text-[#C77D3F]">checklist</span>
            Sub-tasks {items.length > 0 && `· ${items.filter(i => i.checked).length}/${items.length}`}
          </p>
          {items.map(it => (
            <label key={it.id} className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!it.checked} onChange={() => toggleItem(it)} className="accent-[#C77D3F]" />
              <span className={`text-xs ${it.checked ? 'text-gray-400 line-through' : 'text-gray-700'}`}>{it.text}</span>
            </label>
          ))}
          <div className="flex items-center gap-1.5">
            <input value={newItem} onChange={e => setNewItem(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') addItem(); }}
              placeholder="Add sub-task…"
              className="flex-1 border border-gray-200 rounded-md px-2 py-1 text-xs focus:outline-none" />
            <button onClick={addItem} disabled={!newItem.trim()}
              className="h-6 px-2 rounded-md bg-[#C77D3F] text-white text-[10px] font-bold disabled:opacity-40">Add</button>
          </div>
        </div>
        <p className="text-[11px] text-gray-400">Clear the day to send this card back to Unscheduled.</p>
        {isSuggestion && (
          <>
            <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
              <strong>Suggested{card.suggested_by_name ? ` by ${card.suggested_by_name}` : ''}.</strong> The crew won't see this
              until you approve it — edit anything above first if needed.
            </p>
            {/* CR #154 — the note travels back to the suggester with the decision */}
            <textarea value={replyNote} onChange={e => setReplyNote(e.target.value)} rows={2}
              className={`${field} resize-none`}
              placeholder="Reply to them (optional) — sent with your approve/reject" />
          </>
        )}
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 h-10 rounded-lg border border-gray-300 text-sm font-semibold text-gray-600">Cancel</button>
          {isSuggestion ? (
            <>
              <button onClick={() => save('rejected')} disabled={busy}
                className="flex-1 h-10 rounded-lg border border-red-300 text-red-600 text-sm font-semibold disabled:opacity-50">
                Reject
              </button>
              <button onClick={() => save('approved')} disabled={busy}
                className="flex-1 h-10 rounded-lg bg-green-600 text-white text-sm font-semibold disabled:opacity-50">
                {busy ? '…' : 'Approve'}
              </button>
            </>
          ) : (
            <button onClick={() => save()} disabled={busy}
              className="flex-1 h-10 rounded-lg bg-[#C77D3F] text-white text-sm font-semibold disabled:opacity-50">
              {busy ? 'Saving…' : 'Save'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
